/**
 * Performance Tracker
 *
 * Singleton service for measuring timing spans and gauges.
 * Pure TypeScript — no React dependency. Uses ring buffers (200 entries per label)
 * for bounded memory in all-day trading sessions.
 *
 * Follows the globalMarketState pattern from stream.ts.
 */

import type {
  PerfCategory,
  PerfSpan,
  SpanToken,
  PerfGauge,
  PerfStats,
  PerfSnapshot,
} from './types';

const DEFAULT_RING_SIZE = 200;

let nextId = 1;
function generateId(): string {
  return `perf-${nextId++}`;
}

function computePercentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0];
  const idx = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

export class PerfTracker {
  private spans = new Map<string, PerfSpan[]>();
  private gauges = new Map<string, PerfGauge>();
  private listeners = new Set<() => void>();
  private ringSize: number;
  private _enabled = true;
  private _version = 0;
  private _cachedSnapshot: PerfSnapshot | null = null;

  constructor(ringSize: number = DEFAULT_RING_SIZE) {
    this.ringSize = ringSize;
  }

  get enabled(): boolean {
    return this._enabled;
  }

  set enabled(value: boolean) {
    this._enabled = value;
  }

  beginSpan(category: PerfCategory, label: string, metadata?: Record<string, unknown>): SpanToken {
    return {
      _brand: 'SpanToken' as const,
      id: generateId(),
      category,
      label,
      startMs: performance.now(),
      wallClockMs: Date.now(),
      metadata,
    };
  }

  endSpan(token: SpanToken, metadata?: Record<string, unknown>): PerfSpan | null {
    if (!this._enabled) return null;

    const endMs = performance.now();
    const span: PerfSpan = {
      id: token.id,
      category: token.category,
      label: token.label,
      startMs: token.startMs,
      endMs,
      durationMs: endMs - token.startMs,
      wallClockMs: token.wallClockMs,
      metadata: metadata ? { ...token.metadata, ...metadata } : token.metadata,
    };

    const key = `${token.category}:${token.label}`;
    let ring = this.spans.get(key);
    if (!ring) {
      ring = [];
      this.spans.set(key, ring);
    }
    ring.push(span);
    if (ring.length > this.ringSize) {
      ring.shift();
    }

    this._version++;
    this._cachedSnapshot = null;
    this.notify();
    return span;
  }

  setGauge(category: PerfCategory, label: string, value: number, unit: string): void {
    if (!this._enabled) return;

    const key = `${category}:${label}`;
    this.gauges.set(key, { category, label, value, unit, updatedAt: Date.now() });
    this._version++;
    this._cachedSnapshot = null;
    this.notify();
  }

  getStats(category: PerfCategory, label: string): PerfStats | null {
    const key = `${category}:${label}`;
    const ring = this.spans.get(key);
    if (!ring || ring.length === 0) return null;

    const durations = ring.map((s) => s.durationMs);
    const sorted = [...durations].sort((a, b) => a - b);
    const sum = durations.reduce((a, b) => a + b, 0);

    return {
      category,
      label,
      count: ring.length,
      min: sorted[0],
      max: sorted[sorted.length - 1],
      avg: sum / ring.length,
      p50: computePercentile(sorted, 50),
      p95: computePercentile(sorted, 95),
      last: durations[durations.length - 1],
    };
  }

  getAllStats(): PerfStats[] {
    const results: PerfStats[] = [];
    for (const [key, ring] of this.spans) {
      if (ring.length === 0) continue;
      const [category, label] = key.split(':') as [PerfCategory, string];
      const stats = this.getStats(category, label);
      if (stats) results.push(stats);
    }
    return results;
  }

  getGauges(): PerfGauge[] {
    return Array.from(this.gauges.values());
  }

  getSpans(category: PerfCategory, label: string): PerfSpan[] {
    const key = `${category}:${label}`;
    return this.spans.get(key) ?? [];
  }

  snapshot(): PerfSnapshot {
    if (this._cachedSnapshot) return this._cachedSnapshot;
    this._cachedSnapshot = {
      stats: this.getAllStats(),
      gauges: this.getGauges(),
      version: this._version,
    };
    return this._cachedSnapshot;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  clear(): void {
    this.spans.clear();
    this.gauges.clear();
    this._version++;
    this._cachedSnapshot = null;
    this.notify();
  }

  toJSON(): unknown {
    return {
      stats: this.getAllStats(),
      gauges: this.getGauges(),
      version: this._version,
    };
  }

  private notify(): void {
    for (const listener of this.listeners) {
      try {
        listener();
      } catch {
        // Swallow errors in listeners
      }
    }
  }
}
